import { ReactNode } from 'react';
import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ListHeader, { ListHeaderType } from '@/components/ListHeader';
import StatusTag from '@/components/StatusTag';
import { getEndpointWithParams } from '@/routes/config';
import { trioUITheme } from '@/styles/theme';
import Pagination, { PaginationTypes } from '@/trio-ui/Pagination';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import { Job } from '@/types/api';
import { Paths } from '@/types/routes';
import * as Styles from './JobsList.styles';
import { getFormatedCurrency, getDaysDistanceFromDate, getSortByOptions } from './JobsList.utils';
import NewJobForm, { NewJobFormType } from '../NewJobForm';

type JobsListProps = PaginationTypes &
  NewJobFormType &
  ListHeaderType & {
    jobsList: Job[];
    isLoading: boolean;
    isNewJobFormOpened: boolean;
  };

const JobsList = function ({
  onAddNewJob,
  jobsList,
  isLoading,
  onUpdateSortBy,
  onChangePageHandler,
  onChangeRecordsPerPage,
  count,
  itemsPerPage,
  lastPage,
  currentPage,
  isNewJobFormOpened,
  onDirtyForm,
  onShowCandidateFormClick,
  ...props
}: JobsListProps): JSX.Element {
  const renderHeader = (
    <Tr>
      <Th>Partner</Th>
      <Th>Title</Th>
      <Th>When to Start</Th>
      <Th>Time Open</Th>
      <Th>Status</Th>
      <Th># of Talents</Th>
      <Th>Developer Type</Th>
      <Th>Link</Th>
      <Th>Salary Range</Th>
    </Tr>
  );

  const renderRow = (data: Job): ReactNode => {
    const [labelFormated, days] = getDaysDistanceFromDate(data.createdAt);

    const jobLink = getEndpointWithParams(Paths.JOB_DETAILS, {
      jobId: data.id,
    });

    const salaryMin = getFormatedCurrency(data.salaryRange.min);
    const salaryMax = getFormatedCurrency(data.salaryRange.max);

    return (
      <Tr key={data.id}>
        <Td>{data.account?.name}</Td>
        <Td>
          <Styles.TableLink to={jobLink}>{data.title}</Styles.TableLink>
        </Td>
        <Td>{data.whenToStart}</Td>
        <Td textAlign="center">
          <Styles.TimeOpenText days={days}>{labelFormated}</Styles.TimeOpenText>
        </Td>

        <Styles.StatusTd>
          <StatusTag
            status={data.status === 'Active' ? 'Open' : data.status}
            statusColor={trioUITheme.colors['purple-50']}
          />
        </Styles.StatusTd>

        <Td>{data.numberOfDevs}</Td>
        <Td>{data?.developerType}</Td>

        <Td>
          {data?.jobLink ? (
            <a
              aria-label="Open job detail link"
              rel="external noreferrer"
              href={data.jobLink}
              target="_blank"
            >
              <OpenInNewOutlinedIcon sx={{ color: '#054ad9' }} />
            </a>
          ) : (
            <OpenInNewOutlinedIcon sx={{ color: '#D9D9D9' }} />
          )}
        </Td>

        <Styles.SalaryRangeTd>
          $ {salaryMin} <span>to</span> $ {salaryMax}
        </Styles.SalaryRangeTd>
      </Tr>
    );
  };

  const renderEmptyStateRows = (i: number): JSX.Element => (
    <Tr key={i}>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[100px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[100px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[100px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[80px]" />
      </Td>
      <Td>
        <div className="tw-flex tw-items-center">
          <SkeletonCircle className="!tw-w-[5px] !tw-h-[5px] tw-mr-[5px]" />
          <Skeleton className="tw-h-3.5 tw-w-[60px]" />
        </div>
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-6" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[150px]" />
      </Td>
      <Td textAlign="center">
        <OpenInNewOutlinedIcon sx={{ color: '#D9D9D9' }} />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[100px]" />
      </Td>
    </Tr>
  );

  return (
    <Styles.Content data-testid="jobs-list">
      <ListHeader
        {...props}
        isCandidatesSearchBar={false}
        sortByOptions={getSortByOptions()}
        showLinks={false}
        title="Jobs"
        onShowCandidateFormClick={onShowCandidateFormClick}
        onUpdateSortBy={onUpdateSortBy}
      />

      <Styles.NewJobForm data-testid="new-job-form" id="new-job-form" isOpened={isNewJobFormOpened}>
        <NewJobForm
          {...props}
          onAddNewJob={onAddNewJob}
          onDirtyForm={onDirtyForm}
          data-testid={`new-job-form-${isNewJobFormOpened ? 'opened' : 'closed'}`}
          isFormDisabled={!isNewJobFormOpened}
        />
      </Styles.NewJobForm>

      {!jobsList.length && !isLoading ? (
        <Styles.NotFound>
          <Styles.NotFoundSubtitle>Your search did not match any job</Styles.NotFoundSubtitle>
          <p>Try to set different filters.</p>
          <p>You can always use the search bar to find jobs too.</p>
        </Styles.NotFound>
      ) : (
        <>
          {isLoading ? (
            <Table
              renderHeader={renderHeader}
              renderRow={renderEmptyStateRows}
              data={[...Array(itemsPerPage).keys()].map((key) => key + 1)}
            />
          ) : (
            <Table
              customBorderRadius={8}
              customBorderColor={trioUITheme.colors['neutral-40']}
              renderHeader={renderHeader}
              renderRow={renderRow}
              data={jobsList}
            />
          )}
        </>
      )}

      {(isLoading || !!jobsList.length) && (
        <>
          <Pagination
            onChangeRecordsPerPage={onChangeRecordsPerPage}
            lastPage={lastPage}
            itemsPerPage={itemsPerPage}
            count={count}
            currentPage={currentPage}
            onChangePageHandler={onChangePageHandler}
          />
        </>
      )}
    </Styles.Content>
  );
};

export default JobsList;
